import type { LocationObject } from '../../redux/locationSlice';
import {
  verifyProductLocation,
  type VerifiedProductLocation,
} from '../products/newproducts/useCreateProductForm';

/**
 * Pure real-location guard for the wizard's first-product step (US3, T052).
 *
 * Delegates the canonical rules to `verifyProductLocation` (product-creation
 * contract §4) and maps the outcome to explicit UI states. The guard never
 * auto-switches location to make itself pass.
 */

export type FirstProductLocationGuardState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'test_active'; locationName: string }
  | { kind: 'header_mismatch' }
  | { kind: 'ready'; location: VerifiedProductLocation };

export interface FirstProductLocationGuardInput {
  currentLocation: LocationObject | null | undefined;
  isLoading: boolean;
  /** When provided, must agree with the current location id. */
  interceptorLocationId?: string | null;
}

export const evaluateFirstProductLocationGuard = (
  input: FirstProductLocationGuardInput,
): FirstProductLocationGuardState => {
  const verification = verifyProductLocation(input);
  if (verification.ok) {
    return { kind: 'ready', location: verification.location };
  }
  switch (verification.failure) {
    case 'location_loading':
      return { kind: 'loading' };
    case 'location_unavailable':
      return { kind: 'unavailable' };
    case 'test_location_active':
      return {
        kind: 'test_active',
        locationName: String(input.currentLocation?.bp_location_name || ''),
      };
    case 'location_header_mismatch':
      return { kind: 'header_mismatch' };
  }
};

/** Copy shown in the wizard when the guard blocks product creation. */
export const describeFirstProductGuardBlock = (
  state: Exclude<FirstProductLocationGuardState, { kind: 'ready' }>,
): { title: string; body: string } => {
  switch (state.kind) {
    case 'loading':
      return {
        title: 'Getting your location ready',
        body: 'Your business location is still loading. This usually takes a moment.',
      };
    case 'unavailable':
      return {
        title: 'No business location yet',
        body: 'We could not find your business location. Check your connection and try again.',
      };
    case 'test_active':
      return {
        title: 'You are on a test location',
        body: state.locationName
          ? `${state.locationName} is a test location. Products for your business must be added in your real location. Switch locations from Home, then come back.`
          : 'You are on a test location. Products for your business must be added in your real location. Switch locations from Home, then come back.',
      };
    case 'header_mismatch':
      return {
        title: 'Location changed',
        body: 'Your location context changed while preparing this step. Please try again.',
      };
  }
};
